import Link from "next/link";
import { FunctionComponent, useEffect, useState } from "react";
import { useUser } from "../../hooks/useUser";
import { memberSignUp, signUp } from "../../lib/auth-fetcher";
import Button from "../lib/Button";
import { useRedirect } from "../../hooks/useRedirect";
import { hostSlug, reservedAppSlugs } from "../../lib/host";
import { Team, TeamMembershipSettings } from "@prisma/client";
import Avatar from "@xtribe/shared/components/lib/Avatar";
import { cache } from "swr";
import Input from "../lib/Input";
import { useForm } from "react-hook-form";
import CheckBox from "../lib/CheckBox/CheckBox";
import { useRouter } from "next/router";
import Spinner from "@xtribe/shared/components/lib/Spinner";
import Modal from "../lib/Modal";
import InviteCodeModal from "./InviteCodeModal";
import Image from "next/image";

type SignUpFormProps = {
  team?: Team & {
    settings?: { id: string; memberships?: TeamMembershipSettings };
  };
  queryString: string | null;
};

type IFormData = {
  name: string;
  email: string;
  password: string;
  customInviteCode?: string;
  inviteCode?: string;
};

const SignUpForm: FunctionComponent<SignUpFormProps> = ({
  team,
  queryString,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [gdprCompliance, setGdprCompliance] = useState(false);
  const { user, loading, loggedOut, error, mutate } = useUser();
  const { pushToNextPath, getNextPath } = useRedirect({
    defaultPath: "/member",
  });
  const { register, handleSubmit, errors } = useForm<IFormData>();
  const router = useRouter();
  const [invitationRedirect, setInvitationRedirect] = useState("");
  const [signedUp, setSignedUp] = useState(false);
  useEffect(() => {
    const nextPath = getNextPath();
    const regex = /^\/teams\/invite\/[A-Za-z0-9]+$/;
    if (nextPath?.match(regex)) {
      setInvitationRedirect(nextPath);
    } else {
      setInvitationRedirect("");
    }
  }, [router.query]);

  useEffect(() => {
    if (user?.id && !isLoading) {
      const slug = hostSlug(window);
      cache.clear();
      let nextPath =
        slug && !reservedAppSlugs.includes(slug)
          ? getNextPath()
            ? getNextPath()
            : "/member"
          : process.env.NEXT_PUBLIC_APP_URL + "/user";
      if (signedUp && nextPath.includes("/teams/invite")) {
        router.push("/member");
      } else if (team?.id) {
        pushToNextPath(nextPath);
      } else {
        router.push({ pathname: "/tribes/create", query: router.query });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, team]);
  const membershipSettings = team?.settings?.memberships?.join;
  const handleSignUp = async (
    { email, password, name, customInviteCode, inviteCode }: IFormData,
    teamId?: string
  ) => {
    const data = {
      teamId: teamId,
      firstname: name?.trim().split(" ").length
        ? name?.trim().split(" ")[0]
        : undefined,
      lastname:
        name?.trim().split(" ").length && name?.trim().split(" ").length > 1
          ? name?.trim().split(" ")[name?.trim().split(" ").length - 1]
          : "",
      email: email?.trim(),
      password: password?.trim(),
      customInviteCode: customInviteCode?.trim(),
      inviteCode: inviteCode?.trim(),
      gdprCompliance,
    };
    if (teamId) {
      return memberSignUp(data);
    } else {
      return signUp(data);
    }
  };
  async function onSubmit({
    email,
    password,
    name,
    customInviteCode,
  }: IFormData) {
    if (isLoading || loading) return;
    if (!gdprCompliance) {
      return setErrorMsg("Du musst zuerst der Datenschutzerklärung zustimmen.");
    }
    setErrorMsg("");
    setSuccessMsg("");
    let res: { user: any } | any = { user: null };
    if (membershipSettings === "closed" && !invitationRedirect) {
      return setErrorMsg(
        "Du kannst diesem Team nur mit dem Einladungscode aus der Email beitreten"
      );
    } else {
      setIsLoading(true);
      try {
        const inviteCode = invitationRedirect
          ? invitationRedirect.replace("/teams/invite/", "")
          : "";
        res = await handleSignUp(
          {
            email,
            password,
            name,
            customInviteCode,
            inviteCode,
          },
          team?.id
        );
        setSignedUp(true);
        const pushObj = {
          pathname: router.pathname,
          query: {
            ...router.query,
            code: res.user?.inviteCodeSignupId,
          },
        };
        router.push(pushObj, pushObj, { shallow: true });
        await mutate();
        setIsLoading(false);
      } catch (error) {
        setErrorMsg(error.message);
        setSuccessMsg("");
        setIsLoading(false);
      }
      // create user
      // redirect to invite page
    }
  }
  if (loading) {
    return (
      <div className="max-w-md w-full mx-auto py-8 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-md w-full mx-auto">
      <div className="mt-4">
        <div className="flex flex-col justify-center items-center text-center">
          {team ? (
            <Avatar
              storageLink={!!team}
              src={team?.avatar ?? "no-image.svg"}
              size="large"
            />
          ) : (
            <Image
              src="/static/logo/xtribe-logo-128.png"
              height={60}
              width={205.78125}
            />
          )}
          <h1 className="normal-case tracking-tight mb-8 mt-4 h4">
            {team ? (
              <>
                <span className="font-semibold">{team.name}</span> Beitreten
              </>
            ) : (
              "Erstelle dein xTribe"
            )}
          </h1>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-4">
            <div className="w-full">
              <Input
                size="large"
                name="name"
                type="text"
                autoComplete="name"
                width="100%"
                placeholder="Susi Maier"
                ref={register({ required: true })}
              >
                Voller Name
              </Input>
              {errors.name && (
                <p className="text-sm mt-2 text-red-500">
                  {errors.name.message
                    ? errors.name.message
                    : "Dieses Feld wird benötigt"}
                </p>
              )}
            </div>
          </div>
          <div className="mb-4">
            <Input
              size="large"
              name="email"
              type="email"
              autoComplete="email"
              width="100%"
              placeholder="hello@example.com"
              ref={register({
                required: true,
                pattern: {
                  message: "Keine gültige Email",
                  value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                },
              })}
            >
              Email Adresse
            </Input>
            {errors.email && (
              <p className="text-sm mt-2 text-red-500">
                {errors.email.message
                  ? errors.email.message
                  : "Dieses Feld wird benötigt"}
              </p>
            )}
          </div>
          <div className="mb-4">
            <Input
              size="large"
              name="password"
              type="password"
              autoComplete="password"
              placeholder="**********"
              width="100%"
              ref={register({
                required: true,
                minLength: {
                  value: 10,
                  message: "Das Passwort muss mindestens 10 Zeichen lang sein.",
                },
              })}
            >
              Passwort
            </Input>
            {errors.password && (
              <p className="text-sm mt-2 text-red-500">
                {errors.password.message
                  ? errors.password.message
                  : "Dieses Feld wird benötigt"}
              </p>
            )}
          </div>
          {(membershipSettings === "invitation_code" || !membershipSettings) &&
            !invitationRedirect && (
              <div className="mb-6">
                <Input
                  size="large"
                  name="customInviteCode"
                  type="text"
                  placeholder="Einladungscode"
                  width="100%"
                  ref={register({
                    required:
                      ((membershipSettings === "invitation_code" ||
                        !membershipSettings) &&
                        !invitationRedirect) ||
                      !team?.id,
                  })}
                >
                  Einladungscode
                </Input>
                {errors.customInviteCode && (
                  <p className="text-sm mt-2 text-red-500">
                    {errors.customInviteCode.message
                      ? errors.customInviteCode.message
                      : "Dieses Feld wird benötigt"}
                  </p>
                )}
                {!team?.id ? <InviteCodeModal /> : null}
              </div>
            )}
          <div className="flex mb-4">
            <CheckBox
              label="Ja, ich habe die Datenschutzerklärung & AGB's gelesen und stimme diesen zu."
              checked={gdprCompliance}
              onChange={(e) => setGdprCompliance(e.target.checked)}
            />
          </div>
          {errorMsg && <p className="text-red-500 text-sm mb-4">{errorMsg}</p>}
          {!!successMsg && (
            <p className="text-green-500 text-sm mb-4">{successMsg}</p>
          )}
          <div className="flex flex-col ">
            <Button
              type="success"
              htmlType="submit"
              loading={isLoading || loading}
              className="mb-8"
            >
              Account Erstellen
            </Button>
            <p className="text-sm mb-4">
              Schon einen Account?&nbsp;
              <Link href={`/signin${queryString ? "?" + queryString : ""}`}>
                <a className="text-primary font-semibold no-underline hover:underline">
                  Jetzt anmelden
                </a>
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignUpForm;
